import React, { useState } from 'react';
import { 
  History, 
  User, 
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Fuel,
  Shield,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const AssignmentHistory = ({ assignments }) => {
  const [expandedAssignment, setExpandedAssignment] = useState(null);
  const [showAll, setShowAll] = useState(false);

  if (!assignments || assignments.length === 0) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center space-x-3 mb-4">
          <History className="h-5 w-5 text-gray-600" />
          <h4 className="text-sm font-semibold text-gray-900">Assignment History</h4>
        </div>
        <div className="text-center py-6">
          <User className="mx-auto h-8 w-8 text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">No assignment history available</p>
        </div>
      </div>
    );
  }

  const getStatusDisplay = (status) => {
    const statusMap = {
      'active': { 
        icon: CheckCircle, 
        color: 'text-green-600', 
        bg: 'bg-green-100', 
        label: 'Active' 
      },
      'completed': { 
        icon: CheckCircle, 
        color: 'text-blue-600', 
        bg: 'bg-blue-100', 
        label: 'Completed' 
      },
      'terminated': { 
        icon: XCircle, 
        color: 'text-red-600', 
        bg: 'bg-red-100', 
        label: 'Terminated' 
      }
    };
    return statusMap[status] || statusMap['completed'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} days`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingMonths = Math.floor((diffDays % 365) / 30);
      return `${years} year${years > 1 ? 's' : ''}${remainingMonths > 0 ? `, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}` : ''}`;
    }
  };

  const getSafetyScoreColor = (score) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  const displayAssignments = showAll ? assignments : assignments.slice(0, 3);

  return (
    <div className="bg-white rounded-lg border">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <History className="h-5 w-5 text-gray-600" />
            <h4 className="text-sm font-semibold text-gray-900">Assignment History</h4>
            <span className="text-xs text-gray-500">({assignments.length} total)</span>
          </div>
          {assignments.length > 3 && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {showAll ? 'Show Less' : `Show All (${assignments.length})`}
            </button>
          )}
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {displayAssignments.map((assignment, index) => {
          const statusDisplay = getStatusDisplay(assignment.status);
          const isExpanded = expandedAssignment === assignment.id;
          const duration = calculateDuration(assignment.startDate, assignment.endDate);

          return (
            <div key={assignment.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="p-2 bg-gray-100 rounded-full">
                    <User className="h-4 w-4 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h5 className="text-sm font-medium text-gray-900">
                        {assignment.driverName}
                      </h5>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusDisplay.bg} ${statusDisplay.color}`}>
                          {statusDisplay.label}
                        </span>
                        <button
                          onClick={() => setExpandedAssignment(isExpanded ? null : assignment.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(assignment.startDate)} - {formatDate(assignment.endDate)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3" />
                        <span>{duration}</span>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="flex items-center space-x-4 mt-2">
                      {assignment.totalMiles > 0 && (
                        <div className="flex items-center space-x-1 text-xs">
                          <TrendingUp className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-600">{assignment.totalMiles.toLocaleString()} miles</span>
                        </div>
                      )}
                      
                      {assignment.averageMPG > 0 && (
                        <div className="flex items-center space-x-1 text-xs">
                          <Fuel className="h-3 w-3 text-gray-400" />
                          <span className="text-gray-600">{assignment.averageMPG} MPG</span>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-1 text-xs">
                        <Shield className="h-3 w-3 text-gray-400" />
                        <span className={getSafetyScoreColor(assignment.safetyScore)}>
                          {assignment.safetyScore}% safety
                        </span>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h6 className="text-xs font-semibold text-gray-700 mb-2">Assignment Details</h6>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Start Date:</span>
                                <span className="text-gray-900">{formatDate(assignment.startDate)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">End Date:</span>
                                <span className="text-gray-900">{formatDate(assignment.endDate)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Duration:</span>
                                <span className="text-gray-900">{duration}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Status:</span>
                                <span className="text-gray-900">{statusDisplay.label}</span>
                              </div>
                              {assignment.endReason && (
                                <div className="flex justify-between">
                                  <span className="text-gray-500">End Reason:</span>
                                  <span className="text-gray-900">{assignment.endReason}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <h6 className="text-xs font-semibold text-gray-700 mb-2">Performance</h6>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Total Miles:</span>
                                <span className="text-gray-900">{assignment.totalMiles?.toLocaleString() || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Average MPG:</span>
                                <span className="text-gray-900">{assignment.averageMPG || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Safety Score:</span>
                                <span className={getSafetyScoreColor(assignment.safetyScore)}>
                                  {assignment.safetyScore}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      {assignments.length > 0 && (
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <h6 className="text-xs font-semibold text-gray-700 mb-2">Assignment Summary</h6>
          <div className="grid grid-cols-3 gap-4 text-xs text-center">
            <div>
              <div className="font-semibold text-gray-900">{assignments.length}</div>
              <div className="text-gray-500">Total Assignments</div>
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {assignments.filter(a => a.status === 'active').length}
              </div>
              <div className="text-gray-500">Active</div>
            </div>
            <div>
              <div className="font-semibold text-gray-900">
                {Math.round(assignments.reduce((sum, a) => sum + (a.safetyScore || 0), 0) / assignments.length) || 0}%
              </div>
              <div className="text-gray-500">Avg Safety Score</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentHistory;